import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  useGetProfileQuery,
  useGetBalanceQuery,
  useGetTransitWithdrawalAvailabilityQuery,
} from "../store/trader-api";
import { useGetDashboardStatsQuery } from "../store/api";
import { useLocalStorage } from "../hooks/useLocalStorage";

const DashboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 120px);
  padding: 2rem;
  gap: 2rem;

  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem;
    padding-bottom: 80px;
    gap: 0.5rem;
    min-height: auto;
    justify-content: flex-start;
    align-items: stretch;
  }
`;

const PeriodSelector = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background: transparent;
  max-width: 1200px;
  width: 100%;

  @media (max-width: 768px) {
    justify-content: flex-start;
    overflow-x: auto;
    padding: 0 0 0.375rem 0;
    gap: 0.375rem;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
    order: -1;
    flex-shrink: 0;
  }
`;

const PeriodButton = styled.button<{ isActive: boolean }>`
  padding: 0.5rem 1rem;
  border: 1px solid ${(props) => (props.isActive ? "var(--accent-primary)" : "var(--border-color)")};
  border-radius: 0.5rem;
  background: ${(props) => (props.isActive ? "var(--accent-primary)" : "transparent")};
  color: ${(props) => (props.isActive ? "#ffffff" : "var(--text-primary)")};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: ${(props) =>
      props.isActive ? "var(--accent-primary-hover)" : "var(--bg-hover)"};
    border-color: ${(props) => (props.isActive ? "var(--accent-primary-hover)" : "var(--border-input)")};
  }

  @media (max-width: 768px) {
    padding: 0.375rem 0.75rem;
    font-size: 0.8125rem;
  }
`;

const CustomPeriodContainer = styled.div<{ isVisible: boolean }>`
  display: ${(props) => (props.isVisible ? "flex" : "none")};
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background: var(--bg-glass);
  border-radius: 0.5rem;
  border: 1px solid var(--border-divider);

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const DateInput = styled.input`
  background: transparent;
  border: 1px solid var(--border-divider);
  border-radius: 0.375rem;
  padding: 0.375rem 0.5rem;
  color: var(--text-primary);
  font-size: 0.875rem;
  width: 120px;

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
    box-shadow: var(--focus-ring);
  }

  @media (max-width: 768px) {
    width: 110px;
    font-size: 0.8125rem;
  }
`;

const DateSeparator = styled.span`
  color: var(--text-muted);
  font-weight: 500;
`;

const OrdersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-top: 0.75rem;

  @media (max-width: 768px) {
    display: flex;
    gap: 0;
    margin-top: 0.375rem;
    background: var(--bg-secondary);
    border-radius: 0.5rem;
    overflow: hidden;
  }
`;

const OrderMetric = styled.div<{ variant?: "total" | "success" | "failed" }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem;
  background: var(--bg-card-hover);
  border-radius: 0.75rem;
  border: 1px solid var(--border-light);
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: var(--shadow-md);
    background: var(--bg-glass);
  }

  @media (max-width: 768px) {
    flex: 1;
    padding: 0.5rem 0.25rem;
    border-radius: 0;
    border: none;
    background: transparent;
    border-right: 1px solid var(--border-color);

    &:last-child {
      border-right: none;
    }
  }
`;

const OrderMetricValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const OrderMetricLabel = styled.div`
  font-size: 0.75rem;
  color: var(--text-secondary);
  font-weight: 500;
  text-align: center;
  margin-top: 0.25rem;

  @media (max-width: 768px) {
    font-size: 0.5625rem;
  }
`;

const ResetButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: 1px solid var(--accent-error);
  border-radius: 0.5rem;
  background: var(--bg-card);
  color: var(--accent-error);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 1rem;
  font-weight: 600;
  margin-left: 0.5rem;
  opacity: 0;
  animation: fadeIn 0.3s ease-out forwards;

  &:hover {
    background: var(--alert-error-bg);
    color: var(--accent-error);
    border-color: var(--accent-error);
    transform: translateY(-1px);
    box-shadow: var(--shadow-md);
  }

  &:active {
    transform: translateY(0) scale(0.95);
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateX(-10px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @media (max-width: 768px) {
    width: 1.75rem;
    height: 1.75rem;
    font-size: 0.875rem;
    margin-left: 0.375rem;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 1200px;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid var(--accent-primary);
  border-radius: 0.5rem;
  background: transparent;
  color: var(--accent-primary);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: var(--accent-primary);
    color: #ffffff;
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    padding: 0.375rem 0.75rem;
    font-size: 0.8125rem;
  }
`;

const MobileRefreshRow = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.25rem;
    flex-shrink: 0;
  }
`;

const MobileRefreshLabel = styled.span`
  font-size: 0.8125rem;
  color: var(--text-secondary);
  font-weight: 500;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  max-width: 1200px;
  width: 100%;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const StatCard = styled.div`
  background: transparent;
  padding: 2rem 1.5rem;
  border-right: 1px solid var(--border-color);
  border-bottom: 1px solid var(--border-divider);
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &:nth-child(3n) {
    border-right: none;
  }

  &:nth-last-child(-n + 2) {
    border-bottom: none;
  }

  &:hover {
    background: var(--bg-card-hover);
  }

  @media (max-width: 768px) {
    background: var(--bg-card);
    border-radius: 0.75rem;
    border: 1px solid var(--border-divider) !important;
    padding: 0.75rem;
    gap: 0.25rem;
    box-shadow: var(--shadow-sm);
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    margin-bottom: 0.25rem;
    flex-direction: row;
    align-items: center;
    gap: 0.375rem;
    flex-wrap: wrap;
  }
`;

const StatContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const StatValue = styled.div`
  font-size: 2.5rem;
  font-weight: 800;
  color: var(--text-primary);
  line-height: 1.1;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    font-size: 1.25rem;
    margin-bottom: 0;
  }
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-weight: 600;
  line-height: 1.4;
  flex: 1;
`;

const StatChange = styled.div<{ positive: boolean }>`
  font-size: 0.75rem;
  color: var(--accent-primary);
  font-weight: 600;
  padding: 0.25rem 0.75rem;
  background: var(--accent-primary-light);
  border-radius: 0.5rem;
  border: 1px solid var(--accent-primary-border);
  display: inline-block;
  white-space: nowrap;
  transition: all 0.3s ease;

  &:hover {
    background: var(--accent-primary-light);
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    font-size: 0.5625rem;
    padding: 0.125rem 0.375rem;
    white-space: normal;
    line-height: 1.3;
  }
`;

const MobileStatsRow = styled.div`
  display: contents;

  @media (max-width: 768px) {
    display: flex;
    gap: 0.5rem;

    > div {
      flex: 1 1 0;
      min-width: 0;
    }
  }
`;

const BalanceGroup = styled.div`
  display: contents;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    background: var(--bg-card);
    border-radius: 0.75rem;
    border: 1px solid var(--border-divider);
    box-shadow: var(--shadow-sm);
    overflow: hidden;

    > div {
      border: none !important;
      border-radius: 0 !important;
      box-shadow: none !important;

      &:first-child {
        border-bottom: 1px solid var(--border-divider) !important;
      }
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;

  @media (max-width: 480px) {
    flex-wrap: wrap;
  }
`;

const ActionButton = styled.button<{
  disabled?: boolean;
  variant?: "primary" | "secondary";
}>`
  padding: 0.375rem 0.75rem;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s;

  ${({ disabled, variant = "primary" }) => {
    if (disabled) {
      return `
        background: var(--bg-secondary);
        color: var(--text-muted);
        border: 1px solid var(--border-color);
      `;
    }

    if (variant === "secondary") {
      return `
        background: transparent;
        color: var(--accent-primary);
        border: 1px solid var(--accent-primary);
        &:hover {
          background: var(--accent-primary);
          color: #ffffff;
          transform: translateY(-1px);
        }
      `;
    }

    return `
      background: var(--accent-primary);
      color: #ffffff;
      &:hover {
        background: var(--accent-primary-hover);
        transform: translateY(-1px);
      }
    `;
  }}
`;

const TooltipContainer = styled.div`
  position: relative;
  display: inline-flex;
`;

const TooltipBubble = styled.div<{ visible: boolean }>`
  position: absolute;
  bottom: calc(100% + 8px);
  right: 0;
  background: var(--bg-tooltip);
  color: var(--text-tooltip);
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  visibility: ${(props) => (props.visible ? "visible" : "hidden")};
  transform: translateY(${(props) => (props.visible ? "0" : "4px")});
  transition: all 0.2s ease;
  pointer-events: none;
  z-index: 10;
  box-shadow: var(--shadow-md);

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    right: 16px;
    border: 5px solid transparent;
    border-top-color: var(--text-primary);
  }
`;

const InfoIconWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  margin-left: 0.375rem;
`;

const InfoIconSvg = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
  transition: color 0.2s;

  &:hover {
    color: var(--text-secondary);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const InfoPopup = styled.div<{ visible: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%) translateY(${(props) => (props.visible ? "0" : "-4px")});
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 0.75rem;
  padding: 1rem;
  width: 280px;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  visibility: ${(props) => (props.visible ? "visible" : "hidden")};
  transition: all 0.2s ease;
  z-index: 20;
  box-shadow: var(--shadow-lg);

  &::before {
    content: "";
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-bottom-color: var(--border-color);
  }

  &::after {
    content: "";
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-bottom-color: var(--bg-card);
  }
`;

const InfoPopupTitle = styled.div`
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const InfoPopupItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.375rem;
  font-size: 0.75rem;
  color: var(--text-secondary);
  line-height: 1.5;

  &:not(:last-child) {
    margin-bottom: 0.25rem;
  }

  &::before {
    content: "\2022";
    color: var(--text-muted);
    font-weight: bold;
    flex-shrink: 0;
  }
`;

type PeriodType = "24h" | "7d" | "30d" | "all" | "custom";

function Dashboard() {
  const navigate = useNavigate();
  const { data: profile } = useGetProfileQuery();
  const { data: balance, refetch: refetchBalance } = useGetBalanceQuery();
  const { data: transitAvailability } = useGetTransitWithdrawalAvailabilityQuery();

  // Состояние для выбора периода с сохранением в localStorage
  const [selectedPeriod, setSelectedPeriod] = useLocalStorage<PeriodType>(
    "dashboard-selected-period",
    "30d"
  );
  const [customDateFrom, setCustomDateFrom] = useLocalStorage<string>(
    "dashboard-custom-date-from",
    ""
  );
  const [customDateTo, setCustomDateTo] = useLocalStorage<string>(
    "dashboard-custom-date-to",
    ""
  );



  // Получаем параметры для API
  const getPeriodParams = () => {
    switch (selectedPeriod) {
      case "24h":
        return { period: "24h" };
      case "7d":
        return { period: "7d" };
      case "30d":
        return { period: "30d" };
      case "all":
        return { period: "all" };
      case "custom":
        if (customDateFrom && customDateTo) {
          return {
            dateFrom: customDateFrom,
            dateTo: customDateTo,
          };
        }
        return { period: "30d" }; // fallback
      default:
        return { period: "30d" };
    }
  };

  // Получаем параметры для API запроса
  const periodParams = getPeriodParams();
  console.log("📊 Dashboard API params:", periodParams);

  // Получаем статистику дашборда (унифицированно для всех ролей)
  const {
    data: statsData,
    error: statsError,
    isLoading: statsLoading,
    refetch: refetchStats,
  } = useGetDashboardStatsQuery(periodParams);

  console.log("📊 Dashboard stats:", { statsData, statsError, statsLoading });

  const formatUSDT = (amount: number | undefined | null): string => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return "0.00 USDT";
    }
    return `${amount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} USDT`;
  };

  const formatMoney = (
    amount: number | undefined | null,
    currency: string = "USDT"
  ): string => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return `0.00 ${currency}`;
    }
    return `${amount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${currency}`;
  };

  const handleRefresh = () => {
    if (refetchStats) {
      refetchStats();
    }
    if (refetchBalance) {
      refetchBalance();
    }
  };

  // Автообновление при монтировании компонента (при клике в сайдбаре)
  useEffect(() => {
    const refreshData = () => {
      if (refetchStats) refetchStats();
      if (refetchBalance) refetchBalance();
    };

    refreshData();

    window.addEventListener("dashboard-refresh", refreshData);
    return () => window.removeEventListener("dashboard-refresh", refreshData);
  }, [refetchStats, refetchBalance]);

  // Обработчики событий для периодов
  const handlePeriodChange = (period: PeriodType) => {
    console.log("🔄 Changing period to:", period);
    setSelectedPeriod(period);
    if (period !== "custom") {
      setCustomDateFrom("");
      setCustomDateTo("");
    }
  };

  // Обновляем статистику при изменении периода
  useEffect(() => {
    if (refetchStats) {
      refetchStats();
    }
  }, [selectedPeriod, customDateFrom, customDateTo, refetchStats]);

  const handleCustomDateChange = (type: "from" | "to", value: string) => {
    if (type === "from") {
      setCustomDateFrom(value);
    } else {
      setCustomDateTo(value);
    }
  };

  // Функция для сброса фильтров к дефолтному состоянию
  const handleResetFilters = () => {
    setSelectedPeriod("30d"); // Возвращаем к дефолтному периоду
    setCustomDateFrom("");
    setCustomDateTo("");
  };

  // Проверяем, нужно ли показывать кнопку сброса
  const shouldShowResetButton =
    selectedPeriod !== "30d" || customDateFrom !== "" || customDateTo !== "";

  // Используем данные из API дашборда
  const totalOrders = statsData?.totalOrders || 0;
  const completedOrders = statsData?.completedOrders || 0;
  const failedOrders = (statsData?.failedOrders || 0) + (statsData?.cancelledOrders || 0);
  const processedVolume = statsData?.processedVolume || 0;

  // Состояния для тултипов страхового баланса
  const [showWithdrawTooltip, setShowWithdrawTooltip] = useState(false);
  const [showInfoPopup, setShowInfoPopup] = useState(false);


  return (
    <DashboardContainer>
      {/* Mobile refresh row */}
      <MobileRefreshRow>
        <MobileRefreshLabel>Статистика за период</MobileRefreshLabel>
        <RefreshButton onClick={handleRefresh} disabled={statsLoading} style={{ padding: '0.25rem 0.625rem', fontSize: '0.75rem' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 2v6h-6" />
            <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
            <path d="M3 22v-6h6" />
            <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
          </svg>
          {statsLoading ? "..." : "Обновить"}
        </RefreshButton>
      </MobileRefreshRow>

      <Header>
        <div />
        <RefreshButton onClick={handleRefresh} disabled={statsLoading}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 2v6h-6" />
            <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
            <path d="M3 22v-6h6" />
            <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
          </svg>
          {statsLoading ? "Обновление..." : "Обновить"}
        </RefreshButton>
      </Header>
      <StatsGrid>
        <BalanceGroup>
        <StatCard>
          <StatHeader>
            <StatLabel>Рабочий баланс</StatLabel>
            <ButtonGroup>
              <ActionButton onClick={() => navigate("/balance")}>
                Пополнить
              </ActionButton>
              <ActionButton
                variant="secondary"
                onClick={() => navigate("/balance")}
              >
                Вывести
              </ActionButton>
            </ButtonGroup>
          </StatHeader>
          <StatContent>
            <StatValue>
              {formatUSDT(balance?.balance || 0)}
            </StatValue>
            <div
              style={{
                fontSize: "0.75rem",
                color: "var(--text-secondary)",
                marginTop: "0.25rem",
              }}
            >
              В апелляциях: {formatUSDT(balance?.disputeBalance || 0)}
              <br />
              Заморожено: {formatUSDT(balance?.frozenBalance || 0)}
            </div>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatLabel style={{ display: "flex", alignItems: "center" }}>
              Переходный баланс
              <InfoIconWrapper
                onMouseEnter={() => setShowInfoPopup(true)}
                onMouseLeave={() => setShowInfoPopup(false)}
              >
                <InfoIconSvg>
                  <svg viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
                  </svg>
                </InfoIconSvg>
                <InfoPopup visible={showInfoPopup}>
                  <InfoPopupTitle>Условия вывода переходного баланса</InfoPopupTitle>
                  <InfoPopupItem>Нет активных ордеров и апелляций</InfoPopupItem>
                  <InfoPopupItem>Через 7 дней простоя — вывод 50%</InfoPopupItem>
                  <InfoPopupItem>Через 14 дней простоя — вывод 100%</InfoPopupItem>
                  <InfoPopupItem>Новый ордер/апелляция сбрасывает таймер</InfoPopupItem>
                </InfoPopup>
              </InfoIconWrapper>
            </StatLabel>
            <TooltipContainer
              onMouseEnter={() => setShowWithdrawTooltip(true)}
              onMouseLeave={() => setShowWithdrawTooltip(false)}
            >
              <TooltipBubble visible={showWithdrawTooltip && !transitAvailability?.isAvailable}>
                {transitAvailability?.hasActiveOrders || transitAvailability?.hasActiveAppeals
                  ? "Есть активные ордера/апелляции"
                  : !transitAvailability?.thresholdReached
                    ? `Необходимо набрать порог ${formatUSDT(transitAvailability?.transitBalanceThreshold)}`
                    : (balance?.transitBalance || 0) <= 0
                      ? "Баланс пуст"
                      : `До вывода 50%: ${transitAvailability?.daysUntil50 ?? 7} дн.`}
              </TooltipBubble>
              <ActionButton
                disabled={!transitAvailability?.isAvailable || (balance?.transitBalance || 0) <= 0}
                onClick={() => navigate("/balance")}
              >
                Вывести
              </ActionButton>
            </TooltipContainer>
          </StatHeader>
          <StatContent>
            <StatValue>{formatUSDT(balance?.transitBalance || 0)}</StatValue>
            <div
              data-testid="transit-threshold"
              style={{
                fontSize: "0.75rem",
                color: "var(--text-secondary)",
                marginTop: "0.25rem",
                fontWeight: 600,
              }}
            >
              /{" "}
              {formatUSDT(
                transitAvailability?.transitBalanceThreshold ??
                  balance?.transitBalanceThreshold ??
                  0
              )}
            </div>
          </StatContent>
        </StatCard>
        </BalanceGroup>

        <StatCard>
          <StatHeader>
            <StatLabel>Статистика ордеров</StatLabel>
          </StatHeader>
          <StatContent>
            <OrdersGrid>
              <OrderMetric variant="total">
                <OrderMetricValue>{totalOrders}</OrderMetricValue>
                <OrderMetricLabel>Всего</OrderMetricLabel>
              </OrderMetric>
              <OrderMetric variant="success">
                <OrderMetricValue>{completedOrders}</OrderMetricValue>
                <OrderMetricLabel>Успешных</OrderMetricLabel>
              </OrderMetric>
              <OrderMetric variant="failed">
                <OrderMetricValue>{failedOrders}</OrderMetricValue>
                <OrderMetricLabel>Отменённых</OrderMetricLabel>
              </OrderMetric>
            </OrdersGrid>
          </StatContent>
        </StatCard>

        <MobileStatsRow>
        <StatCard>
          <StatHeader>
            <StatLabel>Обработанный объем</StatLabel>
          </StatHeader>
          <StatContent>
            <StatValue>
              {formatMoney(processedVolume)}
            </StatValue>
            <div
              style={{
                fontSize: "0.75rem",
                color: "var(--text-secondary)",
                lineHeight: 1.4,
                marginBottom: "0.5rem",
              }}
            >
              {formatMoney(statsData?.processedVolumeRub, "RUB")}
            </div>
            <div
              style={{
                fontSize: "0.875rem",
                color: "var(--alert-success-text)",
                fontWeight: 600,
                marginTop: "auto",
                paddingTop: "0.5rem",
                borderTop: "1px solid var(--border-color)",
              }}
            >
              Прибыль: {formatMoney(statsData?.netProfit)}
            </div>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatLabel>Процент апелляций</StatLabel>
            <StatChange positive={false}>
              Тех. апелляции / общие транзакции
            </StatChange>
          </StatHeader>
          <StatContent>
            <StatValue>
              {(statsData?.appealPercentage || 0).toFixed(1)}%
            </StatValue>
            <div
              style={{
                fontSize: "0.75rem",
                color: "var(--text-secondary)",
                marginTop: "0.25rem",
              }}
            >
              Тех. апелляции: {statsData?.appealsCount || 0} | Транзакции:{" "}
              {totalOrders}
            </div>
          </StatContent>
        </StatCard>
        </MobileStatsRow>
      </StatsGrid>

      {/* Селектор периодов */}
      <PeriodSelector>
        <PeriodButton
          isActive={selectedPeriod === "24h"}
          onClick={() => handlePeriodChange("24h")}
        >
          24 часа
        </PeriodButton>

        <PeriodButton
          isActive={selectedPeriod === "7d"}
          onClick={() => handlePeriodChange("7d")}
        >
          7 дней
        </PeriodButton>

        <PeriodButton
          isActive={selectedPeriod === "30d"}
          onClick={() => handlePeriodChange("30d")}
        >
          30 дней
        </PeriodButton>

        <PeriodButton
          isActive={selectedPeriod === "all"}
          onClick={() => handlePeriodChange("all")}
        >
          Все время
        </PeriodButton>

        <PeriodButton
          isActive={selectedPeriod === "custom"}
          onClick={() => handlePeriodChange("custom")}
        >
          Период
        </PeriodButton>

        {/* Тайм пикер для кастомного периода */}
        <CustomPeriodContainer isVisible={selectedPeriod === "custom"}>
          <DateInput
            type="date"
            value={customDateFrom}
            onChange={(e) => handleCustomDateChange("from", e.target.value)}
            max={customDateTo || undefined}
          />
          <DateSeparator>—</DateSeparator>
          <DateInput
            type="date"
            value={customDateTo}
            onChange={(e) => handleCustomDateChange("to", e.target.value)}
            min={customDateFrom || undefined}
          />
        </CustomPeriodContainer>

        {/* Кнопка сброса - показывается только когда есть активные фильтры */}
        {shouldShowResetButton && (
          <ResetButton
            onClick={handleResetFilters}
            title="Сбросить фильтры периода"
          >
            ×
          </ResetButton>
        )}
      </PeriodSelector>


    </DashboardContainer>
  );
}

export default Dashboard;
